// src/modals/TechnicalSkillsModal.jsx
/**
 * Technical Skills Modal - Refactored for Production
 * Displays comprehensive technical skills profile with categories and sources
 *
 * Features:
 * - Extracted icon configuration
 * - Memoized skill computations
 * - Shared components
 * - Organized into logical sections
 */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BaseExpandedCard from '../components/cards/BaseExpandedCard';
import { LoadingState, ErrorState, EmptyState, ScoreChip } from '../components/common';
import { useModalData } from '../hooks/useModalData';
import { getConfidenceColor, THEME_COLORS } from '../utils/colorHelpers';
import { formatCategoryName, standardizeEmployeeId } from '../utils/formatters';
import { getCategoryIcon, getSourceIcon } from '../config/skillCategoryIcons';
import { API_BASE_URL } from '../config';

/**
 * SkillCard Component - Displays individual skill with details
 */
const SkillCard = ({ skill }) => (
  <Card
    sx={{
      height: '100%',
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: skill.confidence_level >= 0.8 ? 'rgba(3, 218, 198, 0.3)' : 'rgba(255,255,255,0.1)'
    }}
  >
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
          {skill.name}
        </Typography>
        <ScoreChip value={skill.score.toFixed(1)} color={THEME_COLORS.success} />
      </Box>

      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
          Confidence:
        </Typography>
        <Box
          sx={{
            width: '60%',
            bgcolor: 'rgba(255,255,255,0.1)',
            borderRadius: 1,
            height: 8
          }}
        >
          <Box
            sx={{
              width: `${skill.confidence_level * 100}%`,
              height: '100%',
              borderRadius: 1,
              bgcolor: getConfidenceColor(skill.confidence_level)
            }}
          />
        </Box>
      </Box>

      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Sources:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
          {skill.sources.some(s => s.type === 'project' && s.current) && (
            <Chip
              icon={React.createElement(getSourceIcon('project'), { fontSize: 'small' })}
              label="Current"
              size="small"
              sx={{ fontSize: '0.6rem', height: 20 }}
            />
          )}
          {skill.sources.some(s => s.type === 'project' && !s.current) && (
            <Chip
              icon={React.createElement(getSourceIcon('project'), { fontSize: 'small' })}
              label="Past"
              size="small"
              sx={{ fontSize: '0.6rem', height: 20 }}
            />
          )}
          {skill.sources.some(s => s.type === 'certification') && (
            <Chip
              icon={React.createElement(getSourceIcon('certification'), { fontSize: 'small' })}
              label="Cert"
              size="small"
              sx={{ fontSize: '0.6rem', height: 20 }}
            />
          )}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

SkillCard.propTypes = {
  skill: PropTypes.shape({
    name: PropTypes.string.isRequired,
    score: PropTypes.number.isRequired,
    confidence_level: PropTypes.number.isRequired,
    sources: PropTypes.array.isRequired
  }).isRequired
};

/**
 * TechnicalSkillsModal Component
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether modal is open
 * @param {Function} props.onClose - Close handler
 * @param {string} props.employeeName - Employee display name
 * @param {string} props.employeeId - Employee identifier
 */
const TechnicalSkillsModal = ({ open, onClose, employeeName, employeeId, tabMode = false }) => {
  const [tableSort, setTableSort] = useState({ field: 'score', dir: 'desc' });

  const handleTableSort = (field) => {
    setTableSort(prev =>
      prev.field === field
        ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { field, dir: field === 'score' ? 'desc' : 'asc' }
    );
  };

  // Standardize employee ID
  const standardizedId = useMemo(() => {
    return standardizeEmployeeId(employeeId);
  }, [employeeId]);

  // Fetch technical skills data
  const { data: skillsData, loading, error } = useModalData(
    `${API_BASE_URL}/employees/${standardizedId}/technical-skills`,
    open,
    { cache: true, ttl: 10 * 60 * 1000 } // 10 minute cache
  );

  // Memoize skills extraction and sorting
  const { sortedSkills, topSkills, categorizedSkills, categoryInfo, metadata } = useMemo(() => {
    const skills = skillsData?.skills || [];
    const sorted = [...skills].sort((a, b) => b.score - a.score);
    const top = sorted.filter(skill => skill.score >= 8);

    return {
      sortedSkills: sorted,
      topSkills: top,
      categorizedSkills: skillsData?.categorized_skills || {},
      categoryInfo: skillsData?.category_info || {},
      metadata: skillsData?.meta_data || {}
    };
  }, [skillsData]);

  const tableSortedSkills = useMemo(() => {
    const arr = [...sortedSkills];
    if (tableSort.field === 'name') {
      arr.sort((a, b) => tableSort.dir === 'asc'
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name));
    } else {
      arr.sort((a, b) => tableSort.dir === 'asc' ? a.score - b.score : b.score - a.score);
    }
    return arr;
  }, [sortedSkills, tableSort]);

  const Wrap = ({ children }) => tabMode
    ? <>{children}</>
    : <BaseExpandedCard open={open} onClose={onClose} title={`Technical Skills: ${employeeName}`} subtitle="Technical capabilities, certifications and project experience">{children}</BaseExpandedCard>;

  if (loading) return <Wrap><LoadingState message="Fetching technical skills data..." /></Wrap>;
  if (error) return <Wrap><ErrorState message={error} /></Wrap>;

  return (
    <Wrap>
      {sortedSkills.length > 0 ? (
        <Box>
          {/* Top Skills Section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Top Technical Skills
            </Typography>

            <Grid container spacing={2}>
              {topSkills.slice(0, 8).map((skill, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <SkillCard skill={skill} />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Skills by Category Section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Skills by Category
            </Typography>

            {Object.entries(categorizedSkills).map(([categoryId, categorySkills]) => {
              if (!categorySkills || categorySkills.length === 0) return null;

              const categoryName = categoryInfo[categoryId]?.name || formatCategoryName(categoryId);
              const IconComponent = getCategoryIcon(categoryId);

              return (
                <Accordion
                  key={categoryId}
                  sx={{
                    mb: 1,
                    bgcolor: 'background.paper',
                    '&:before': { display: 'none' }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ bgcolor: 'rgba(255,255,255,0.03)' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', color: 'primary.main' }}>
                        <IconComponent />
                      </Box>
                      <Typography variant="subtitle1">{categoryName}</Typography>
                      <Chip label={categorySkills.length} size="small" sx={{ ml: 2, height: 20 }} />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {categorySkills.map((skillName, index) => {
                        const skillDetails = sortedSkills.find(s => s.name === skillName);

                        return (
                          <Chip
                            key={index}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography sx={{ fontSize: '0.75rem' }}>{skillName}</Typography>
                                {skillDetails && (
                                  <Typography
                                    sx={{
                                      fontSize: '0.7rem',
                                      ml: 0.5,
                                      color: getConfidenceColor(skillDetails.confidence_level || 0)
                                    }}
                                  >
                                    {skillDetails.score ? skillDetails.score.toFixed(1) : ''}
                                  </Typography>
                                )}
                              </Box>
                            }
                            sx={{
                              bgcolor: 'rgba(255,255,255,0.1)',
                              border: '1px solid rgba(255,255,255,0.05)'
                            }}
                          />
                        );
                      })}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>

          {/* Skills Detail Table (Top 20) */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Skill Details & Sources
            </Typography>

            <TableContainer component={Paper} sx={{ bgcolor: 'background.paper' }}>
              <Table size="small" aria-label="Skills details table">
                <TableHead>
                  <TableRow>
                    <TableCell sortDirection={tableSort.field === 'name' ? tableSort.dir : false}>
                      <TableSortLabel
                        active={tableSort.field === 'name'}
                        direction={tableSort.field === 'name' ? tableSort.dir : 'asc'}
                        onClick={() => handleTableSort('name')}
                      >
                        Skill
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={tableSort.field === 'score' ? tableSort.dir : false}>
                      <TableSortLabel
                        active={tableSort.field === 'score'}
                        direction={tableSort.field === 'score' ? tableSort.dir : 'desc'}
                        onClick={() => handleTableSort('score')}
                      >
                        Score
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Confidence</TableCell>
                    <TableCell>Categories</TableCell>
                    <TableCell>Sources</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableSortedSkills.map((skill, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{skill.name}</TableCell>
                      <TableCell align="center">
                        <ScoreChip value={skill.score.toFixed(1)} color={THEME_COLORS.success} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box
                            sx={{
                              width: 50,
                              bgcolor: 'rgba(255,255,255,0.1)',
                              borderRadius: 1,
                              height: 8,
                              mr: 1
                            }}
                          >
                            <Box
                              sx={{
                                width: `${skill.confidence_level * 100}%`,
                                height: '100%',
                                borderRadius: 1,
                                bgcolor: getConfidenceColor(skill.confidence_level)
                              }}
                            />
                          </Box>
                          <Typography variant="caption">
                            {(skill.confidence_level * 100).toFixed(0)}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {skill.categories && skill.categories.length > 0 ? (
                            skill.categories.map((catId, catIndex) => {
                              const IconComponent = getCategoryIcon(catId);
                              return (
                                <Chip
                                  key={catIndex}
                                  icon={<IconComponent />}
                                  label={categoryInfo[catId]?.name || formatCategoryName(catId)}
                                  size="small"
                                  sx={{ fontSize: '0.65rem', height: 20 }}
                                />
                              );
                            })
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              None
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {skill.sources && skill.sources.map((source, sourceIndex) => {
                            let label = '';
                            let IconComponent = null;

                            if (source.type === 'project') {
                              label = source.current ? `Current: ${source.name}` : `Past: ${source.name}`;
                              IconComponent = getSourceIcon('project');
                            } else if (source.type === 'certification') {
                              label = `Cert: ${source.name}`;
                              IconComponent = getSourceIcon('certification');
                            } else if (source.type === 'training') {
                              label = `Training: ${source.name}`;
                              IconComponent = getSourceIcon('training');
                            }

                            return (
                              <Chip
                                key={sourceIndex}
                                icon={IconComponent ? <IconComponent fontSize="small" /> : null}
                                label={label}
                                size="small"
                                sx={{ fontSize: '0.65rem', height: 20 }}
                              />
                            );
                          })}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

          </Box>

          {/* Metadata Section */}
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="caption" color="text.secondary">
              Calculated on: {metadata.last_calculated ? new Date(metadata.last_calculated).toLocaleString() : 'Unknown'} •
              Projects analyzed: {metadata.total_projects_analyzed || 0} •
              Certifications: {metadata.total_certifications_analyzed || 0}
            </Typography>
          </Box>
        </Box>
      ) : (
        <EmptyState
          title="No technical skills data available"
          message="This employee doesn't have any inferred technical skills yet."
        />
      )}
    </Wrap>
  );
};

TechnicalSkillsModal.propTypes = {
  /** Whether the modal is open */
  open: PropTypes.bool.isRequired,
  /** Function to call when modal should close */
  onClose: PropTypes.func.isRequired,
  /** Employee display name for title */
  employeeName: PropTypes.string.isRequired,
  /** Employee identifier for API calls */
  employeeId: PropTypes.string.isRequired
};

export default TechnicalSkillsModal;
